import { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SidebarLayout } from '@/layouts/sidebar/SidebarLayout';
import { Permission } from '@/models/user';
import { PageWithLayout } from '@/models/page';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useCreateTagMutation,
    useDeleteTagMutation,
    useGetTagsQuery,
    useReorderTagsMutation,
    useUpdateTagMutation,
} from '@/api/v2/tags';
import { useNotification } from '@starjack/features/notifications';
import {
    isConflict,
    saveErrorMessage,
    usageCount,
} from '../filterTags/tagErrors';
import { ProductTag } from '@/models/product';

const SortableRow: FunctionComponent<{
    tag: ProductTag;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ tag, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: tag.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <Card ref={setNodeRef} style={style} sx={{ p: 2 }}>
            <Stack direction='row' spacing={2} alignItems='center'>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{ cursor: 'grab', display: 'flex' }}
                >
                    <DragIndicatorIcon color='action' />
                </Box>
                <Typography variant='subtitle1' sx={{ flex: 1 }}>
                    {tag.name}
                </Typography>
                <IconButton onClick={onEdit} size='small'>
                    <EditIcon />
                </IconButton>
                <IconButton onClick={onDelete} size='small' color='error'>
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Card>
    );
};

const TagsPage: PageWithLayout = () => {
    // liftOperatorData is null until the stored session is rehydrated — destructuring it
    // straight away crashes the whole app on a direct hit or a reload of this route.
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;
    const { addNotification } = useNotification();
    const { data: tags = [] } = useGetTagsQuery(
        { liftOperatorId },
        { skip: !liftOperatorId }
    );
    const [createTag] = useCreateTagMutation();
    const [updateTag] = useUpdateTagMutation();
    const [deleteTag] = useDeleteTagMutation();
    const [reorderTags] = useReorderTagsMutation();

    const [order, setOrder] = useState<string[]>([]);
    // Reorder requests are chained and counted: overlapping drags would otherwise race, and the refetch each of
    // them triggers would push a stale server order back into the list while the user is still dragging.
    const reorderQueue = useRef<Promise<unknown>>(Promise.resolve());
    const reordersInFlight = useRef(0);

    useEffect(() => {
        if (reordersInFlight.current > 0) return;
        setOrder(tags.map((t) => t.id));
    }, [tags]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const [editing, setEditing] = useState<ProductTag | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState<ProductTag | null>(
        null
    );
    const [name, setName] = useState('');

    const orderedTags = order
        .map((id) => tags.find((t) => t.id === id))
        .filter((t): t is ProductTag => t != null);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = order.indexOf(String(active.id));
        const newIndex = order.indexOf(String(over.id));
        const newOrder = arrayMove(order, oldIndex, newIndex);
        setOrder(newOrder);

        reordersInFlight.current += 1;
        reorderQueue.current = reorderQueue.current
            .then(() =>
                reorderTags({
                    liftOperatorId,
                    orderedIds: newOrder,
                }).unwrap()
            )
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: 'Reihenfolge konnte nicht gespeichert werden',
                });
                // Drop back to the last order the server confirmed instead of leaving an unsaved one on screen.
                setOrder(tags.map((t) => t.id));
            })
            .finally(() => {
                reordersInFlight.current -= 1;
            });
    };

    const openEdit = (tag: ProductTag) => {
        setEditing(tag);
        setName(tag.name);
    };

    const handleSave = async () => {
        if (!name.trim()) return;
        try {
            if (editing) {
                await updateTag({
                    liftOperatorId,
                    tagId: editing.id,
                    payload: { name: name.trim() },
                }).unwrap();
            } else {
                await createTag({
                    liftOperatorId,
                    payload: { name: name.trim() },
                }).unwrap();
            }
            setEditing(null);
            setCreateOpen(false);
            setName('');
        } catch (err) {
            addNotification({
                type: 'error',
                message: saveErrorMessage(err, 'einen Tag'),
            });
        }
    };

    const handleDelete = async (force: boolean) => {
        if (!deleteCandidate) return;
        try {
            await deleteTag({
                liftOperatorId,
                tagId: deleteCandidate.id,
                force,
            }).unwrap();
            setDeleteCandidate(null);
        } catch (err) {
            // Offer the force delete on any conflict, not only when the payload carries a usage count.
            if (isConflict(err) && !force) {
                const count = usageCount(err);
                const inUse = count
                    ? `Dieser Tag ist in ${count} Produkten in Verwendung.`
                    : 'Dieser Tag ist noch in Verwendung.';
                if (confirm(`${inUse} Trotzdem löschen?`)) {
                    await handleDelete(true);
                }
            } else {
                addNotification({
                    type: 'error',
                    message: 'Löschen fehlgeschlagen',
                });
            }
        }
    };

    return (
        <Box sx={{ p: 4 }}>
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                mb={3}
            >
                <Typography variant='h4'>Tags verwalten</Typography>
                <Button
                    variant='contained'
                    onClick={() => {
                        setName('');
                        setCreateOpen(true);
                    }}
                >
                    Neuer Tag
                </Button>
            </Stack>
            <Typography color='text.secondary' mb={3}>
                Tags sind sprachunabhängige Marketing-Slugs für URL-Steering im
                Webshop.
            </Typography>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={order}
                    strategy={verticalListSortingStrategy}
                >
                    <Stack spacing={1}>
                        {orderedTags.map((tag) => (
                            <SortableRow
                                key={tag.id}
                                tag={tag}
                                onEdit={() => openEdit(tag)}
                                onDelete={() => setDeleteCandidate(tag)}
                            />
                        ))}
                    </Stack>
                </SortableContext>
            </DndContext>

            <Dialog
                open={editing != null || createOpen}
                onClose={() => {
                    setEditing(null);
                    setCreateOpen(false);
                }}
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle>
                    {editing ? 'Tag bearbeiten' : 'Neuer Tag'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label='Tag-Name'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setEditing(null);
                            setCreateOpen(false);
                        }}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={!name.trim()}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteCandidate != null}
                onClose={() => setDeleteCandidate(null)}
            >
                <DialogTitle>Tag löschen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Soll der Tag „{deleteCandidate?.name}“ gelöscht werden?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteCandidate(null)}>
                        Abbrechen
                    </Button>
                    <Button
                        color='error'
                        variant='contained'
                        onClick={() => handleDelete(false)}
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

TagsPage.isProtected = true;
TagsPage.getLayout = (page) => <SidebarLayout>{page}</SidebarLayout>;
TagsPage.permissions = [Permission.LIFTOPERATOR_CONFIGURATION_NAV];

export default TagsPage;
